import { Component, ComponentInterface, Element, h, Host, Prop } from '@stencil/core';
import { addEventListener, closestElement, removeEventListener } from '../../utils/helpers';

@Component({
  tag: 'vds-pagination-progress',
  styleUrl: 'vds-pagination-progress.scss',
  shadow: true,
})
export class VdsPaginationProgress implements ComponentInterface {
  @Element() el: HTMLVdsPaginationProgressElement;

  /**
   * Max value of the progress bar. Automatically set when slides reference is found.
   */
  @Prop({ mutable: true, reflect: true }) max: number = 0;

  /**
   * Current value of the progress bar. Automatically set when slides reference is found.
   */
  @Prop({ mutable: true, reflect: true }) value: number = 1;

  /**
   * Connected Slides HTML Element
   */
  @Prop({ mutable: true }) slides?: HTMLVdsSlidesElement;

  /**
   * CSS Selector Reference to Slides HTML Element
   */
  @Prop() slidesRef?: string;

  render() {
    const { value, max } = this;

    return (
      <Host>
        { max > 1 && (
          <div class="flex flex-wrap w-[250px] items-center justify-center sm:border sm:border-grey-lighter sm:rounded-[500px] sm:px-3 sm:py-1 bg-white">
            <div class="w-full max-w-full min-w-[1px] box-border flex-[1] hidden sm:block">
              <button class="block float-left overflow-visible leading-[0] text-grey-lighter hover:text-berry" disabled={value <= 1} onClick={() => this.slidePrev()}>
                <svg version="1.1" xmlns="http://www.w3.org/2000/svg" x="0" y="0" viewBox="0 0 10 20" xmlSpace="preserve" width="10" height="20">
                  <path
                    d="M9,1L2,8c-1.1,0.9-1.3,2.5-0.4,3.6C1.7,11.7,1.8,11.9,2,12l7,7"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  ></path>
                </svg>
              </button>
            </div>
            <div class="w-full sm:w-3/4 box-border max-w-full sm:py-[20px]">
              <progress value={value} max={max}></progress>
            </div>
            <div class="w-full max-w-full min-w-[1px] box-border flex-[1] hidden sm:block">
              <button class="block float-right overflow-visible leading-[0] text-grey-lighter hover:text-berry" disabled={value >= max} onClick={() => this.slideNext()}>
                <svg xmlns="http://www.w3.org/2000/svg" data-name="2px_Line_Icons" viewBox="59 54 9.97 20" width="9.97" height="20">
                  <path d="M60,73l7-7a2.54,2.54,0,0,0,0-4l-7-7" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"></path>
                </svg>
              </button>
            </div>
          </div>
        )}
      </Host>
    );
  }

  async connectedCallback(): Promise<void> {
    const slides = this.slides ? this.slides : (this.slides = this.getSlidesByReference(this.slidesRef));

    if (slides) {
      this.max = slides.childElementCount;
      this.value = (await slides.getActiveIndex()) + 1;
      addEventListener(slides, 'vdsSlideDidChange', () => this.onSlideDidChange(slides));
    }
  }

  disconnectedCallback(): void {
    const slides = this.slides;
    removeEventListener(slides, 'vdsSlideDidChange', () => this.onSlideDidChange(slides));
  }

  private getSlidesByReference(ref: string): HTMLVdsSlidesElement {
    if (ref) {
      return document.querySelector<HTMLVdsSlidesElement>(ref);
    } else {
      return (closestElement('vds-slides', this.el) as HTMLVdsSlidesElement) ?? document.querySelector('vds-slides');
    }
  }

  private async onSlideDidChange(slides: HTMLVdsSlidesElement): Promise<void> {
    const index = await slides.getActiveIndex();
    this.value = index + 1;
  }

  private slideNext(): void {
    if (this.slides) {
      this.slides.slideNext();
    }
    else if (this.value < this.max) {
      this.value++;
    }
  }

  private slidePrev(): void {
    if (this.slides) {
      this.slides.slidePrev();
    }
    else if (this.value > 1) {
      this.value--;
    }
  }
}
